import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Lock } from "lucide-react";
import { api, errMsg, fmtL, ROLES } from "@/lib/api";
import { PageHeader, Spinner, Badge } from "@/components/opl/Common";
import { ResultsLockCard } from "@/components/opl/ResultsLock";
import { Field, GoldBtn, GhostBtn, useList } from "@/components/opl/Crud";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

function Seasons() {
  const [rows, load] = useList("/seasons");
  const [name, setName] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex gap-2"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New season name" data-testid="new-season-input" />
        <GhostBtn testid="create-season-btn" disabled={!name} onClick={async () => { try { await api.post("/seasons", { name, year: new Date().getFullYear() }); setName(""); load(); toast.success("Season created"); } catch (e) { toast.error(errMsg(e)); } }}><Plus className="h-4 w-4" />Create</GhostBtn></div>
      {(rows || []).map((s) => (
        <div key={s.id} className="flex justify-between items-center text-sm bg-black/20 rounded-lg px-3 py-2" data-testid={`season-${s.name}`}>
          <span>{s.name}</span>
          {s.active ? <Badge className="text-emerald-300 border-emerald-500/40">Active</Badge> :
            <button className="text-[#E5B842] text-xs" data-testid={`activate-season-${s.name}`} onClick={async () => { try { await api.post(`/seasons/${s.id}/activate`); load(); window.location.reload(); } catch (e) { toast.error(errMsg(e)); } }}>Activate</button>}
        </div>
      ))}
    </div>
  );
}

export default function AuctionConfig() {
  const [c, setC] = useState(null);
  const [cats] = useList("/categories");
  const [pf, setPf] = useState(null);
  const [reason, setReason] = useState("");
  const loadPf = async () => {
    try {
      const clubs = (await api.get("/clubs")).data.filter((x) => x.active !== false);
      const res = await Promise.all(clubs.map((cl) => api.get(`/auction/${cl.id}/preflight`).then((r) => ({ cl, d: r.data }))));
      setPf({ ok: res.every((x) => x.d.ok), checks: res.flatMap(({ cl, d }) => d.checks.map((k) => ({ ...k, key: `${cl.id}-${k.key}`, label: `${cl.name}: ${k.label}` }))) });
    } catch {
      setPf({ ok: false, checks: [] });
    }
  };
  useEffect(() => { api.get("/config").then((r) => setC(r.data)); loadPf(); }, []);
  if (!c) return <Spinner />;
  const set = (k) => (v) => setC((s) => ({ ...s, [k]: v }));
  const F = (name, label, type = "number", extra = {}) => <Field f={{ name, label, type, ...extra }} value={c[name]} onChange={set(name)} />;
  const pub = (k) => (v) => setC((s) => ({ ...s, public: { ...s.public, [k]: v } }));
  const inc = c.bid_increments || [];
  const setInc = (i, k, v) => set("bid_increments")(inc.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const save = async () => {
    try {
      const r = await api.put("/config", { ...c, reason });
      setC(r.data);
      setReason("");
      toast.success("Configuration saved");
      loadPf();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Auction Configuration" crumbs={["Configuration"]} eyebrow={c.season_label}>
        {c.locked && <span className="flex items-center gap-1 text-xs text-orange-300"><Lock className="h-3 w-3" />Critical settings locked (session active)</span>}
        {c.locked && <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for change" className="w-56" data-testid="config-reason-input" />}
        <GoldBtn testid="save-config-btn" onClick={save}>Save configuration</GoldBtn>
      </PageHeader>
      <ResultsLockCard />
      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <Tabs defaultValue="basics">
            <TabsList className="bg-[#0B1323] flex-wrap h-auto">
              {[["basics", "1 · Season"], ["purse", "2 · Purse & squad"], ["bidding", "3 · Bidding & timer"], ["order", "4 · Rounds & order"], ["limits", "5 · Limits"], ["procedures", "6 · Procedures"], ["public", "7 · Public display"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`config-tab-${v}`}>{l}</TabsTrigger>)}
            </TabsList>
            <div className="surface rounded-2xl p-6 mt-3">
              <TabsContent value="basics" className="grid sm:grid-cols-2 gap-4">
                {F("title", "Auction title", "text")}{F("season_label", "Season label", "text")}
                {F("auction_scheduled_at", "Auction start (for public countdown)", "datetime-local")}
                <div className="sm:col-span-2"><p className="eyebrow mb-2">Seasons</p><Seasons /></div>
              </TabsContent>
              <TabsContent value="purse" className="grid sm:grid-cols-2 gap-4">
                {F("team_purse", "Team purse (points)")}
                {F("apply_purse_to_teams", "Apply purse change to all teams on save", "switch")}
                {F("min_squad", "Minimum squad size")}{F("max_squad", "Maximum squad size")}
                {F("enforce_purse_reserve", "Reserve purse for minimum squad", "switch", { hint: "Max bid = remaining purse − (slots still required × lowest base price)" })}
                {F("low_purse_threshold", "Low purse alert below (points)")}
                {F("owner_bidding_enabled", "Team owners can bid from phones", "switch", { hint: "Off = only the auctioneer places bids with team buttons" })}
                <div className="sm:col-span-2"><p className="eyebrow mb-2">Minimum players per role (advisory warnings)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{ROLES.map((r) => <Field key={r} f={{ name: `role_${r}`, label: r, type: "number" }} value={c.role_minimums?.[r] ?? 0} onChange={(v) => set("role_minimums")({ ...c.role_minimums, [r]: v || 0 })} />)}</div>
                </div>
              </TabsContent>
              <TabsContent value="bidding" className="space-y-5">
                <div>
                  <p className="eyebrow mb-2">Bid increment slabs</p>
                  {inc.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2" data-testid={`increment-slab-${i}`}>
                      <span className="text-sm text-slate-400 w-28">While bid below</span>
                      <Input type="number" step="any" value={s.upto ?? ""} placeholder="no limit" className="w-28" onChange={(e) => setInc(i, "upto", e.target.value === "" ? null : Number(e.target.value))} data-testid={`slab-upto-${i}`} />
                      <span className="text-sm text-slate-400">pts, raise by</span>
                      <Input type="number" step="any" value={s.increment} className="w-24" onChange={(e) => setInc(i, "increment", Number(e.target.value))} data-testid={`slab-inc-${i}`} />
                      <span className="text-sm text-slate-400">pts</span>
                      <button onClick={() => set("bid_increments")(inc.filter((_, j) => j !== i))} className="p-2 text-slate-500"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <GhostBtn testid="add-slab-btn" onClick={() => set("bid_increments")([...inc, { upto: null, increment: 1 }])}><Plus className="h-4 w-4" />Add slab</GhostBtn>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {F("bid_timer_seconds", "Bid countdown (s)")}{F("extension_window_seconds", "Extension window (final s)")}{F("extension_seconds", "Extend countdown to (s)")}
                  {F("going_once_at", "GOING ONCE at (s left)")}{F("going_twice_at", "GOING TWICE at (s left)")}{F("reset_timer_on_bid", "Reset full timer on every bid", "switch")}
                </div>
              </TabsContent>
              <TabsContent value="order" className="grid sm:grid-cols-2 gap-4">
                {F("rounds", "Number of auction rounds")}
                {F("nomination_order", "Nomination order", "select", { options: [{ value: "category", label: "By category order, then player code" }, { value: "manual", label: "Manual lot order" }, { value: "random", label: "Spinning wheel (random draw)" }] })}
                {F("unsold_procedure", "Unsold players", "select", { options: [{ value: "next_round", label: "Return to pool in next round" }, { value: "withdraw", label: "Withdraw from auction" }] })}
              </TabsContent>
              <TabsContent value="limits" className="space-y-4">
                <p className="eyebrow">Maximum players per category per team (0 = no limit)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{(cats || []).map((k) => <Field key={k.id} f={{ name: `cl_${k.name}`, label: `${k.name} (${fmtL(k.base_price)})`, type: "number" }} value={c.category_limits?.[k.id] ?? 0} onChange={(v) => set("category_limits")({ ...c.category_limits, [k.id]: v || 0 })} />)}</div>
                <div className="grid sm:grid-cols-2 gap-4">{F("special_player_tag", "Special / overseas player tag", "text", { hint: "Players carrying this tag count toward the limit" })}{F("special_player_limit", "Max special players per team (0 = off)")}</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {F("retention_enabled", "Retention enabled", "switch", { hint: "Recorded in rulebook only — not automatically enforced" })}
                  {F("rtm_enabled", "Right to Match enabled", "switch", { hint: "Recorded in rulebook only — not automatically enforced" })}
                </div>
              </TabsContent>
              <TabsContent value="procedures" className="grid sm:grid-cols-2 gap-4">
                {F("tie_breaking", "Tie-breaking procedure", "textarea", { full: true })}
                {F("dispute_procedure", "Dispute procedure", "textarea", { full: true })}
                {F("squad_deadline", "Final squad submission deadline", "datetime-local")}
              </TabsContent>
              <TabsContent value="public" className="grid sm:grid-cols-2 gap-4">
                <Field f={{ name: "show_purse", label: "Show team purses publicly", type: "switch" }} value={c.public?.show_purse} onChange={pub("show_purse")} />
                <Field f={{ name: "show_results", label: "Publish auction results", type: "switch" }} value={c.public?.show_results} onChange={pub("show_results")} />
                <Field f={{ name: "allow_results_download", label: "Allow results download", type: "switch" }} value={c.public?.allow_results_download} onChange={pub("allow_results_download")} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
        <div className="surface-2 rounded-2xl p-5 h-fit" data-testid="config-preflight">
          <p className="eyebrow">Preflight checklist</p>
          <h3 className={`text-xl font-bold mt-1 ${pf?.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="preflight-status">{pf?.ok ? "Ready to start" : "Auction start blocked"}</h3>
          <div className="mt-3 space-y-1.5">{pf?.checks.map((k) => <div key={k.key} className="text-sm flex justify-between gap-3"><span className={k.ok ? "text-emerald-300" : k.blocking ? "text-red-300" : "text-orange-300"}>{k.ok ? "✓" : k.blocking ? "✕" : "!"} {k.label}</span><span className="text-xs text-slate-500 text-right">{k.detail}</span></div>)}</div>
        </div>
      </div>
    </div>
  );
}
